import { ItemCollectInfoSystem } from '@phaser/systems/ItemCollectInfo.system';
import { MultiplayerSystem } from '@phaser/systems/Multiplayer.system';
import { ItemDropObject } from '@phaser/objects/ItemDrop.object';
import { AnnounceSystem } from '@phaser/systems/Announce.system';
import { MinigameSystem } from '@phaser/systems/Minigame.system';
import { GameModeSystem } from '@phaser/systems/GameMode.system';
import { ContinueSystem } from '@phaser/systems/Continue.system';
import { RespawnSystem } from '@phaser/systems/Respawn.system';
import { AbilitySystem } from '@phaser/systems/Ability.system';
import { AttackSystem } from '@phaser/systems/Attack.system';
import { FishingMinigame } from '@phaser/minigames/fishing';
import { ComboSystem } from '@phaser/systems/Combo.system';
import { InputSystem } from '@phaser/systems/Input.system';
import { ScaleSystem } from '@phaser/systems/Scale.system';
import { PartySystem } from '@phaser/systems/Party.system';
import { MiningMinigame } from '@phaser/minigames/mining';
import { CardObject } from '@phaser/objects/Card.object';
import { loadFont } from '@phaser/helpers/FontHelper';
import { useLobbyStore, useAppStore } from '@stores';
import { Scene } from 'phaser';
import i18next from 'i18next';

export let CARD_WIDTH = 100;
export let CARD_HEIGHT = 150;
export let PARTY_OFFSET = 22;
export const GAP = 10;

export class Game extends Scene {
  public static current: Game;

  public grid: { x: number, y: number }[] = [];
  public items: ItemDropObject[] = [];
  public cards: CardObject[] = [];
  public deadIds: string[] = [];

  public id = useAppStore.getState().id;

  public itemCollectInfoSystem: ItemCollectInfoSystem;
  public multiplayerSystem: MultiplayerSystem;
  public fishingMinigame: FishingMinigame;
  public announceSystem: AnnounceSystem;
  public minigameSystem: MinigameSystem;
  public miningMinigame: MiningMinigame;
  public gameModeSystem: GameModeSystem;
  public continueSystem: ContinueSystem;
  public respawnSystem: RespawnSystem;
  public abilitySystem: AbilitySystem;
  public attackSystem: AttackSystem;
  public comboSystem: ComboSystem;
  public inputSystem: InputSystem;
  public scaleSystem: ScaleSystem;
  public partySystem: PartySystem;

  constructor() {
    super('Game');
  }

  preload() {
    this.load.setPath('assets');

    this.load.multiatlas('sprites', 'sprites.json');

    this.load.image('water', 'backgrounds/water.avif');
    this.load.image('cave', 'backgrounds/cave.avif');

    loadFont('Ascentian', 'fonts/Ascentian.woff2');
  }

  public getGridPos(x: number, y: number): number {
    const camera = this.cameras.main;
    const availableWidth = camera.displayWidth;
    const availableHeight = camera.displayHeight;

    const xOffset = (availableWidth - (CARD_WIDTH * 3) - (GAP * 4)) / 2;
    const yOffset = (availableHeight - (CARD_HEIGHT * 3) - (GAP * 4)) / 2;

    const relativeX = x - xOffset;
    const relativeY = y - yOffset;

    const col = Math.floor((relativeX - GAP) / (CARD_WIDTH + GAP));
    const row = Math.floor((relativeY - GAP) / (CARD_HEIGHT + GAP));

    if (col < 0 || col > 2 || row < 0 || row > 2) {
      return -1;
    }

    const cellX = GAP + col * (CARD_WIDTH + GAP);
    const cellY = GAP + row * (CARD_HEIGHT + GAP);

    if (relativeX >= cellX && 
      relativeX <= cellX + CARD_WIDTH && 
      relativeY >= cellY && 
      relativeY <= cellY + CARD_HEIGHT) {
      return row * 3 + col;
    }

    return -1;
  }

  async create() {
    Game.current = this;

    this.itemCollectInfoSystem = new ItemCollectInfoSystem();
    this.multiplayerSystem = new MultiplayerSystem();
    this.fishingMinigame = new FishingMinigame();
    this.miningMinigame = new MiningMinigame();
    this.minigameSystem = new MinigameSystem();
    this.announceSystem = new AnnounceSystem();
    this.gameModeSystem = new GameModeSystem();
    this.continueSystem = new ContinueSystem();
    this.respawnSystem = new RespawnSystem();
    this.abilitySystem = new AbilitySystem();
    this.attackSystem = new AttackSystem();
    this.comboSystem = new ComboSystem();
    this.scaleSystem = new ScaleSystem();
    this.inputSystem = new InputSystem();
    this.partySystem = new PartySystem();

    window.onbeforeunload = function() {
      return i18next.t('Game.leave');
    };

    setTimeout(() => {
      this.createGrid();
      this.multiplayerSystem.ready();
    }, 250);
  }

  public update(time: number, delta: number) {
    this.attackSystem.update(time, delta);
    this.respawnSystem.update(time, delta);
    this.gameModeSystem.update(time, delta);
    this.minigameSystem.update(time, delta);
    this.continueSystem.update(time, delta);
    this.fishingMinigame.update(time, delta);
    this.itemCollectInfoSystem.update(time, delta);
  }

  public destroy() {
    this.multiplayerSystem.destroy();
    this.fishingMinigame.destroy();
    this.gameModeSystem.destroy();
    this.miningMinigame.destroy();
    this.respawnSystem.destroy();
    this.comboSystem.destroy();

    this.cards.forEach(card => card.destroy());

    window.onbeforeunload = function() {};
  }

  private createGrid() {
    const { state } = useLobbyStore.getState();
    PARTY_OFFSET = state!.players.length > 1 ? 22 : 0;

    const camera = this.cameras.main;
    const availableWidth = camera.displayWidth;
    const availableHeight = camera.displayHeight - PARTY_OFFSET;

    CARD_WIDTH = Math.min(150, (availableWidth - GAP * 4) / 3);
    CARD_HEIGHT = Math.min(300, (availableHeight - GAP * 4) / 3);

    const xOffset = (availableWidth - (CARD_WIDTH * 3) - (GAP * 4)) / 2;
    const yOffset = (availableHeight - (CARD_HEIGHT * 3) - (GAP * 4)) / 2;

    this.grid = [];
    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        const x = GAP + col * (CARD_WIDTH + GAP) + CARD_WIDTH / 2 + xOffset;
        const y = PARTY_OFFSET + GAP + row * (CARD_HEIGHT + GAP) + CARD_HEIGHT / 2 + yOffset;
        this.grid.push({ x, y });
      }
    }
  }
}